/**
 * MenuOrderListCard — anchor port (devourplay-anchor/menu-orders.html).
 *
 * Single row in the Order History list with an expand-in-place
 * receipt panel. Native <button> chrome — the legacy button retired per
 * `the no-legacy-components rule`. Status semantics use the
 * design-system color tokens directly (amber/green/gray pills, red
 * is brand/CTA only).
 *
 * Action button rules:
 *   - Physical merchant + processing  → "Track" red primary
 *   - LootShop (digital) + processing → no button (waiting on us)
 *   - LootShop (digital) + completed  → "Reorder" red primary
 *   - Anything else completed         → "Receipt" ghost
 *   - Cancelled / errored             → "Details" ghost
 *
 * Icon tile:
 *   - LootShop (digital) → red shopping-bag glyph on surface tile
 *     (NEVER the legacy purple gradient merchant.icon — that's the
 *     visual leak that made the half-port look like un-themed
 *     legacy)
 *   - Other merchants → merchant.icon.url framed in the anchor tile
 */

import { ReactElement, ReactNode, useState } from "react";
import { useNavigate } from "react-router";
import {
    HandoffOptions,
    MenuOrder,
    MenuOrderItem,
    MenuOrderStatus,
} from "@devour/client";
import moment from "moment-timezone";
import classNames from "classnames";
import { FaChevronDown, FaExternalLinkAlt, FaShoppingBag, FaStore } from "react-icons/fa";

import { useMerchant } from "@/hooks/useMerchant";
import { DEVOUR_MERCHANT_ID } from "@/constants/merchantConstants";

import {
    getOrderStatusBucket,
    processingList,
} from "../../pages/menu-orders/MenuOrderListPage";
import OrderReceiptMenuItem from "../OrderReceiptMenuItem";
import MenuOrderCheckoutTotals from "./MenuOrderCheckoutTotals";

interface Props {
    order: MenuOrder;
}

function MenuOrderListCard(props: Props): ReactElement {
    const navigate = useNavigate();
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const { data: merchant } = useMerchant(props.order.business);
    const merchantTimeZone = merchant?.timeZone || moment.tz.guess();

    if (!props.order) return null;

    const sumQuantity: number = props.order?.orderItems.reduce((acc, item) => acc + item.quantity, 0);

    const isDigitalOrder =
        props.order.handoff === HandoffOptions.DIGITAL || props.order.business === DEVOUR_MERCHANT_ID;

    const isLootShopOrder = props.order.business === DEVOUR_MERCHANT_ID;

    const bucket = getOrderStatusBucket(props.order);
    const isProcessing = bucket === "progress";
    const isComplete = bucket === "complete";
    const isCancel = bucket === "cancel";

    const isValidRedemptionUrl = (url: string): boolean => {
        try {
            const parsed = new URL(url);
            return parsed.protocol === "http:" || parsed.protocol === "https:";
        } catch {
            return false;
        }
    };

    const hasRedemptionUrls =
        isDigitalOrder &&
        props.order.orderItems.some(item =>
            item.redemptionUrls?.some(url => url?.trim() && isValidRedemptionUrl(url)));

    function toggleExpanded(e: React.MouseEvent): void {
        e.stopPropagation();
        setIsExpanded(!isExpanded);
    }

    function handleKeyDown(e: React.KeyboardEvent): void {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsExpanded(!isExpanded);
        }
    }

    /*
     * This button only renders for LootShop orders (see the isComplete &&
     * isLootShopOrder branch below), so it always lands on /lootshop.
     *
     * It used to navigate to `${merchant.url}/${placeId}`. For every other
     * merchant that route is still correct, but the LootShop merchant is the
     * one id MerchantRedirectHandler does NOT redirect — it renders the legacy
     * MerchantPage instead, so LootShop reorders dead-ended on the old
     * storefront rather than the current /lootshop surface.
     *
     * The cart write is gone with it. This handler called
     * createMenuOrderItems(order) with the raw previous order — the "old blind
     * reorder" LootShopPage.dropUnpriceableGiftCards() exists to clean up after:
     * a gift card carries no price of its own, so replaying one persists a $0
     * item and every later cart write 400s with "Gift card amount is required".
     * /lootshop's own Order Again path merges into an existing cart and re-opens
     * the customization modal to re-confirm the amount, which is the flow this
     * button should be handing the user to rather than racing.
     */
    function handleReorder(e: React.MouseEvent) {
        e.stopPropagation();
        navigate("/lootshop");
    }

    function handleTrackOrder(e: React.MouseEvent) {
        e.stopPropagation();
    }

    const brandLabel = isLootShopOrder ? "LootShop" : merchant?.name ?? "Order";

    /*
     * Display status: simplify the wide MenuOrderStatus enum into
     * three reader-friendly bands. Physical-delivery progress gets a
     * tighter label so the pill reads as a delivery state, not a
     * generic "processing".
     */
    let statusLabel = "Processing";
    if (isComplete) statusLabel = "Completed";
    else if (isCancel) statusLabel = "Cancelled";
    else if (props.order.status === MenuOrderStatus.PARTIALLY_DELIVERED) {
        // Mixed LootBox + gift-card order: LootBoxes are in the Stash, gift cards still pending.
        statusLabel = "Partially delivered";
    } else if (!isDigitalOrder && processingList.includes(props.order.status)) {
        statusLabel = "Out for delivery";
    }

    const dateLine = moment(props.order.updatedAt).tz(merchantTimeZone)
        .format("MMM DD YYYY · hh:mm A")
        .toUpperCase();
    const itemCountLine = `${sumQuantity} ${sumQuantity === 1 ? "item" : "items"}`;
    const totalLabel = isCancel ? "Refunded" : "Total";
    const totalValue = `$${props.order.subtotal}`;

    function renderOrderItem(item: MenuOrderItem, i: number): ReactNode {
        return <OrderReceiptMenuItem item={item} key={`receipt-item_${i}`} />;
    }

    function renderRedemptionLinks(): ReactNode {
        if (!hasRedemptionUrls) return null;

        const urls: Array<{ itemName: string; url: string; index: number }> = [];
        props.order.orderItems.forEach(item => {
            item.redemptionUrls?.forEach((url, idx) => {
                if (url?.trim() && isValidRedemptionUrl(url)) {
                    urls.push({ itemName: item.name, url, index: idx });
                }
            });
        });

        return (
            <div className="dp-redeem">
                <div className="dp-redeem__head">
                    <span className="dp-redeem__pill">Ready</span>
                    <span className="dp-redeem__title">Gift Card Links</span>
                </div>
                <p className="dp-redeem__sub">Click a link to redeem. Codes are single-use.</p>
                <div className="dp-redeem__list">
                    {urls.map((u, i) =>
                        <a
                            key={`redemption-url-${i}`}
                            href={u.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="dp-redeem__link"
                            onClick={e => e.stopPropagation()}
                        >
                            <span className="dp-redeem__link-name">
                                <span>{u.itemName}</span>
                                <span className="idx">#{u.index + 1}</span>
                            </span>
                            <FaExternalLinkAlt />
                        </a>)}
                </div>
            </div>
        );
    }

    /*
     * Action button — gated per order type + status. Cancelled orders
     * get a Details ghost. Digital orders in flight get no button.
     */
    function renderAction(): ReactNode {
        if (isCancel) {
            return <button type="button" className="dp-order__action is-ghost" onClick={e => e.stopPropagation()}>Details</button>;
        }
        if (isProcessing && !isDigitalOrder) {
            return (
                <button
                    type="button"
                    className="dp-order__action"
                    onClick={(e) => {
                        handleTrackOrder(e);
                        navigate(`/menu-orders/${props.order.id}/success`);
                    }}
                >
                    Track
                </button>
            );
        }
        if (isComplete && isLootShopOrder) {
            return <button type="button" className="dp-order__action" onClick={handleReorder}>Reorder</button>;
        }
        if (isComplete) {
            return <button type="button" className="dp-order__action is-ghost" onClick={e => e.stopPropagation()}>Receipt</button>;
        }
        /* Digital processing → no button, just keep the column slot. */
        return <span className="dp-order__action-slot" />;
    }

    return (
        <article className={classNames("dp-order", { "is-expanded": isExpanded })}>
            <button
                type="button"
                className="dp-order__row"
                onClick={toggleExpanded}
                onKeyDown={handleKeyDown}
                aria-expanded={isExpanded}
            >
                <div
                    className={classNames("dp-order__icon", {
                        "is-lootshop": isLootShopOrder,
                        "is-merchant": !isLootShopOrder && !isDigitalOrder,
                    })}
                    aria-hidden="true"
                >
                    {isLootShopOrder
                        ? <FaShoppingBag />
                        : merchant?.icon?.url
                            ? <img src={merchant.icon.url} alt="" />
                            : <FaStore />}
                </div>

                <div className="dp-order__main">
                    <span className="dp-order__brand">{brandLabel}</span>
                    <span className="dp-order__meta">
                        <span
                            className={classNames("dp-status-pill", {
                                "is-progress": isProcessing,
                                "is-complete": isComplete,
                                "is-cancel": isCancel,
                            })}
                        >
                            {statusLabel}
                        </span>
                        <span className="dot">·</span>
                        <strong>{itemCountLine}</strong>
                        <span className="dot">·</span>
                        <span>{dateLine}</span>
                    </span>
                </div>

                <div className="dp-order__total">
                    <span className="dp-order__total-lbl">{totalLabel}</span>
                    {totalValue}
                </div>

                {renderAction()}

                <FaChevronDown className="dp-order__chevron" />
            </button>

            {isExpanded &&
                <div className="dp-order__expand">
                    <div className="dp-receipt-id">
                        <span>Order <strong>#{props.order?.orderUuid || props.order?.pickupCode}</strong></span>
                        <span className="ts">{dateLine}</span>
                    </div>

                    {renderRedemptionLinks()}

                    <div className="dp-items">
                        <h4 className="dp-items__hd">{sumQuantity} {sumQuantity === 1 ? "Item" : "Items"}</h4>
                        {props.order.orderItems.map(renderOrderItem)}
                    </div>

                    <div className="dp-totals">
                        <MenuOrderCheckoutTotals menuOrder={props.order} />
                    </div>
                </div>
            }
        </article>
    );
}

export default MenuOrderListCard;
